import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, User, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import Button from '../Common/Button/Button';
import Logo from '../../../assets/Images/haatzaSellerlogo.png';
import './Navbar.css';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const navLinksRef = useRef(null);
  const location = useLocation();

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScrollable = () => {
    const el = navLinksRef.current;
    if (!el) return;
    const isOverflowing = el.scrollWidth > el.clientWidth + 8;
    if (!isOverflowing) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
    } else {
      setCanScrollLeft(el.scrollLeft > 8);
      setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 8);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollPos = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
      if (scrollPos > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    checkScrollable();
    window.addEventListener('resize', checkScrollable);
    const el = navLinksRef.current;
    if (el) {
      el.addEventListener('scroll', checkScrollable, { passive: true });
    }
    return () => {
      window.removeEventListener('resize', checkScrollable);
      if (el) el.removeEventListener('scroll', checkScrollable);
    };
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setActiveDropdown(null);
    setTimeout(checkScrollable, 100);
  }, [location.pathname]);

  const handleScrollLeft = () => {
    if (navLinksRef.current) {
      navLinksRef.current.scrollBy({ left: -160, behavior: 'smooth' });
    }
  };

  const handleScrollRight = () => {
    if (navLinksRef.current) {
      navLinksRef.current.scrollBy({ left: 160, behavior: 'smooth' });
    }
  };

  const toggleDropdown = (dropdown) => {
    if (activeDropdown === dropdown) {
      setActiveDropdown(null);
    } else {
      setActiveDropdown(dropdown);
    }
  };

  const isPathActive = (path) => {
    if (!path) return false;
    const current = location.pathname.toLowerCase().replace(/\/$/, '') || '/';
    const target = path.toLowerCase().replace(/\/$/, '') || '/';
    return current === target;
  };

  const navLinks = [

    { name: 'Sell Online', path: '/sellonline' },
    { name: 'Grow', path: '/grow' },
    { name: 'How it works', path: '/how-it-works' },
    { name: 'Pricing & Commission', path: '/pricing' },
    { name: 'Shipping & Returns', path: '/shipping' },
    { name: 'Seller Advertising Hub', path: '/advertising' }
  ];

  return (
    <nav className={`hz-navbar ${scrolled ? 'hz-navbar-scrolled' : ''}`}>
      {location.pathname === '/' && (
        <div className="home-navbar-top-bar">
          <span className="home-navbar-top-text">
            Existing Seller? Explore our product recommendations
          </span>
          <Link to="/signin" className="home-navbar-top-login-btn">
            Login
          </Link>
        </div>
      )}
      <div className="container hz-nav-container">
        {/* Section 1: Logo */}
        <div className="hz-nav-logo">
          <Link to="/" className="hz-logo-link">
            <img
              src={Logo}
              alt="Haatza Seller Logo"
              className="hz-logo-image"
            />
          </Link>
        </div>

        {/* Section 2: Module Names with Tablet Horizontal Scroll Controls */}
        <div className="hz-nav-links-wrapper">
          {canScrollLeft && (
            <button
              type="button"
              className="hz-nav-scroll-btn hz-nav-scroll-left"
              onClick={handleScrollLeft}
              aria-label="Scroll left nav items"
            >
              <ChevronLeft size={18} />
            </button>
          )}

          <ul className="hz-nav-links" ref={navLinksRef}>
            {navLinks.map((link, idx) => (
              <li
                key={idx}
                className={`hz-nav-item ${link.dropdown ? 'hz-has-dropdown' : ''}`}
                onMouseEnter={link.dropdown ? () => setActiveDropdown(link.name) : undefined}
                onMouseLeave={link.dropdown ? () => setActiveDropdown(null) : undefined}
              >
                {link.dropdown ? (
                  <>
                    <button
                      type="button"
                      onClick={() => toggleDropdown(link.name)}
                      className={`hz-nav-link-btn ${activeDropdown === link.name ? 'active' : ''}`}
                    >
                      {link.name} <ChevronDown size={14} className="chevron" />
                    </button>
                    <AnimatePresence>
                      {activeDropdown === link.name && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          transition={{ duration: 0.15 }}
                          className="hz-dropdown-menu glass-panel-heavy"
                        >
                          {link.items.map((item, subIdx) => {
                            const Icon = item.icon;
                            return (
                              <Link
                                to={item.path || `#${item.name.toLowerCase().replace(/ /g, '-')}`}
                                key={subIdx}
                                className="hz-dropdown-item"
                              >
                                <div className="hz-dropdown-icon-wrapper">
                                  <Icon size={18} className="hz-dropdown-icon" />
                                </div>
                                <div className="hz-dropdown-info">
                                  <div className="hz-dropdown-title">{item.name}</div>
                                  <div className="hz-dropdown-desc">{item.desc}</div>
                                </div>
                              </Link>
                            );
                          })}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                ) : (
                  <Link
                    to={link.path}
                    className={`hz-nav-link-a ${isPathActive(link.path) ? 'active' : ''}`}
                  >
                    {link.name}
                  </Link>
                )}
              </li>
            ))}
          </ul>

          {canScrollRight && (
            <button
              type="button"
              className="hz-nav-scroll-btn hz-nav-scroll-right"
              onClick={handleScrollRight}
              aria-label="Scroll right nav items"
            >
              <ChevronRight size={18} />
            </button>
          )}
        </div>

        {/* Section 3: Start Selling Button */}
        <div className="hz-nav-start-selling-wrapper">
          <Link to="/signin" className="hz-nav-start-selling" style={{ textDecoration: 'none' }}>
            <Button variant="primary" size="sm">
              Start Selling
            </Button>
          </Link>
        </div>

        {/* Section 4: Log In */}
        <div className="hz-nav-login-wrapper">
          <Link to="/signin" className="hz-login-link">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="#007BFF" className="hz-login-icon">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 4c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" />
            </svg>
            <span className="hz-login-text">Log In</span>
          </Link>
        </div>

        {/* Mobile Hamburger Menu Toggle */}
        <button
          type="button"
          className="hz-mobile-toggle-btn"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle Menu"
        >
          {mobileMenuOpen ? <X size={30} /> : <Menu size={30} strokeWidth={2.2} />}
        </button>
      </div>

      {/* Mobile Sidebar & Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="hz-mobile-backdrop"
              onClick={() => setMobileMenuOpen(false)}
            />
            <motion.div
              initial={{ x: location.pathname === '/' ? '100%' : '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: location.pathname === '/' ? '100%' : '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 250 }}
              className="hz-mobile-nav-drawer"
            >
              <div className="hz-mobile-drawer-header">
                <button
                  type="button"
                  className="hz-mobile-close-btn"
                  onClick={() => setMobileMenuOpen(false)}
                  aria-label="Close menu"
                >
                  <X size={22} color="#ffffff" />
                </button>
              </div>

              <ul className="hz-mobile-links">
                {navLinks.map((link, idx) => (
                  <li key={idx} className="hz-mobile-nav-item">
                    {link.dropdown ? (
                      <div className="hz-mobile-dropdown-group">
                        <div className="hz-mobile-dropdown-header">{link.name}</div>
                        <div className="hz-mobile-dropdown-subitems">
                          {link.items.map((item, subIdx) => (
                            <Link
                              to={item.path || `#${item.name.toLowerCase().replace(/ /g, '-')}`}
                              key={subIdx}
                              onClick={() => setMobileMenuOpen(false)}
                              className="hz-mobile-subitem"
                            >
                              {item.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <Link
                        to={link.path}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`hz-mobile-link-a ${isPathActive(link.path) ? 'active' : ''}`}
                      >
                        {link.name}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </nav>
  );
}
